import { useMemo } from "react";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import {
  InactiveStoreListQuery,
  InactiveStoreResponse,
} from "../../types/inactive-stores.type";
import { SURFACE } from "../common/designTokens";
import { DaysCell, LastSaleCell } from "./StoreCells";
import StorePagination from "./StorePagination";
import {
  EMPTY_TEXT,
  LIST_LOAD_ERROR_TEXT,
  MISSING,
  PivotCellSelection,
  cellQuery,
  cellTitle,
  showsStatusColumn,
  statusLabel,
  storeCountLabel,
} from "./inactiveStores";
import { useInactiveStorePage } from "./useInactiveStorePage";

const TABLE_SX = { "& td, & th": { whiteSpace: "nowrap" } } as const;
const HEAD_SX = { "& th": { bgcolor: SURFACE.head, fontWeight: 600 } } as const;
const NAME_CELL_SX = { whiteSpace: "normal !important", minWidth: 180 } as const;
const TITLE_SX = { pr: 6 } as const;
const CLOSE_BUTTON_SX = { position: "absolute", right: 8, top: 8 } as const;
const SKELETON_ROW_COUNT = 5;

interface DialogColumn {
  header: string;
  align?: "right";
}

const STORE_COLUMNS: DialogColumn[] = [
  { header: "CODE" },
  { header: "ชื่อร้าน" },
  { header: "van" },
  { header: "GROUP" },
  { header: "ขายล่าสุด" },
  { header: "จำนวนวัน", align: "right" },
];
const WITH_STATUS_COLUMNS: DialogColumn[] = [...STORE_COLUMNS, { header: "สถานะ" }];

interface StoreListDialogProps {
  open: boolean;
  /** The count that was clicked. Kept after closing so the dialog does not blank as it fades. */
  cell: PivotCellSelection | null;
  days: number;
  lookbackDays: number;
  onClose: () => void;
}

/**
 * The shops behind one pivot count. The server sorts shops with no bill first, then the
 * longest without one. A view only: finance exports from the detail table.
 */
export default function StoreListDialog({
  open,
  cell,
  days,
  lookbackDays,
  onClose,
}: Readonly<StoreListDialogProps>) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      {cell && (
        <DialogTitle sx={TITLE_SX}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h6" component="span">
              {cellTitle(cell)}
            </Typography>
            <Chip size="small" color="primary" label={storeCountLabel(cell.count)} />
          </Stack>
          <IconButton aria-label="ปิด" onClick={onClose} sx={CLOSE_BUTTON_SX}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
      )}
      <DialogContent dividers>
        {cell && <StoreList cell={cell} days={days} lookbackDays={lookbackDays} />}
      </DialogContent>
    </Dialog>
  );
}

interface StoreListProps {
  cell: PivotCellSelection;
  days: number;
  lookbackDays: number;
}

function StoreList({ cell, days, lookbackDays }: Readonly<StoreListProps>) {
  const query = useMemo<InactiveStoreListQuery>(
    () => ({ days, branch_code: cell.branch?.branch_code, ...cellQuery(cell.column) }),
    [cell, days],
  );
  const { rows, count, page, setPage, loading, hasError } = useInactiveStorePage(query);
  const hasStatusColumn = showsStatusColumn(cell.column);
  const columns = hasStatusColumn ? WITH_STATUS_COLUMNS : STORE_COLUMNS;
  const isEmpty = !loading && rows.length === 0;

  return (
    <>
      {hasError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {LIST_LOAD_ERROR_TEXT}
        </Alert>
      )}
      <TableContainer>
        <Table size="small" sx={TABLE_SX}>
          <TableHead sx={HEAD_SX}>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.header} align={column.align}>
                  {column.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              Array.from({ length: SKELETON_ROW_COUNT }, (_row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {columns.map((column) => (
                    <TableCell key={column.header}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {isEmpty && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    {EMPTY_TEXT}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              rows.map((store) => (
                <DialogStoreRow
                  key={store.customer_uuid}
                  store={store}
                  lookbackDays={lookbackDays}
                  hasStatusColumn={hasStatusColumn}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <StorePagination count={count} page={page} onPageChange={setPage} />
    </>
  );
}

interface DialogStoreRowProps {
  store: InactiveStoreResponse;
  lookbackDays: number;
  hasStatusColumn: boolean;
}

function DialogStoreRow({ store, lookbackDays, hasStatusColumn }: Readonly<DialogStoreRowProps>) {
  return (
    <TableRow hover>
      <TableCell>{store.customer_code}</TableCell>
      <TableCell sx={NAME_CELL_SX}>{store.customer_name ?? MISSING}</TableCell>
      <TableCell>{store.van}</TableCell>
      <TableCell>{store.shop_group}</TableCell>
      <LastSaleCell date={store.last_sale_date} />
      <DaysCell days={store.days_since_sale} lookbackDays={lookbackDays} />
      {hasStatusColumn && <TableCell>{statusLabel(store.is_disabled)}</TableCell>}
    </TableRow>
  );
}
